import { useUserStore } from '../../../store/useUserStore';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Drawer } from '@mui/material';


import {
  COMMUNITY_QNA_TYPE,
  COMMUNITY_USER_TYPE,
  COMPLAINT_TARGET_TYPE,
} from '../../../data/define';
import { useQnaListSearchInfiniteQuery } from '../../../queries';
import useInfiniteScroll from '../../../hook/useInfiniteScroll';
import PostCard from './PostCard';
import PostDetail from './PostDetail';
import OptionDialog from './OptionDialog';
import ComplaintDialog from '../ComplaintDialog';
import HighLightableBtn from '../../common/HighLightableBtn';
import OneBtnAlert from '../../common/OneBtnAlert';
import { useTranslation } from '../../../hook/useTranslation';

const OrderType = {
  Likes: 'likes',
  Latest: 'createdAt',
};

const LIMIT = 10;

function Qna() {
  const user = useUserStore();
  const { t } = useTranslation();
  const pushedRef = useRef(false);
  const [optionOpenId, setOptionOpenId] = useState(null);
  const [complaintOpenId, setComplaintOpenId] = useState(null);
  const [contentOpenId, setContentOpenId] = useState(null);
  const [order, setOrder] = useState(OrderType.Latest);
  const [categorySorting, setCategorySorting] = useState<
    (typeof COMMUNITY_QNA_TYPE)[keyof typeof COMMUNITY_QNA_TYPE]
  >(COMMUNITY_QNA_TYPE.ALL);
  const [analysisUserTypeSorting, setAnalysisUserTypeSorting] = useState<
    (typeof COMMUNITY_USER_TYPE)[keyof typeof COMMUNITY_USER_TYPE]
  >(COMMUNITY_USER_TYPE.ALL);
  const [privatePostModalOpen, setPrivatePostModalOpen] = useState(false);

  // TanStack Query 무한 스크롤 적용
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useQnaListSearchInfiniteQuery({
    limit: LIMIT,
    questionType: categorySorting,
    userType: analysisUserTypeSorting,
    order,
  });

  const list = useMemo(
    () => data?.pages.flatMap((page: any) => page.items) || [],
    [data]
  );


  // infinite scroll
  const { lastItemRef } = useInfiniteScroll({
    hasMore: !!hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
  });

  // 뒤로가기에 드로워가 닫히도록 설정
  const openDrawer = useCallback(
    (id: number) => {
      const post = list.find((item: any) => item.id === id);

      // 비공개글이고 로그인 유저의 글이 아닐 때 모달 표시
      if (post?.isPrivate && post?.author?.id !== user?.id) {
        setPrivatePostModalOpen(true);
        return;
      }

      // 그 외의 경우 정상적으로 드로워 오픈
      setContentOpenId(id);
      window.history.pushState({ drawer: true }, '');
      pushedRef.current = true;
    },
    [list, user]
  );

  const closeDrawer = useCallback(() => {
    setContentOpenId(null);
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (contentOpenId !== null) {
        setContentOpenId(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [contentOpenId]);

  return (
    <div className="relative w-full">
      <div className="w-full flex justify-between mt-5">
        <div className="flex justify-start items-center text-[12px] font-[700] gap-1">
          <HighLightableBtn
            isActive={categorySorting === COMMUNITY_QNA_TYPE.ALL}
            onClick={() => setCategorySorting(COMMUNITY_QNA_TYPE.ALL)}
            label={t('community.qna.total')}
          />
          <HighLightableBtn
            isActive={categorySorting === COMMUNITY_QNA_TYPE.ANALYSIS}
            onClick={() => setCategorySorting(COMMUNITY_QNA_TYPE.ANALYSIS)}
            label={t('community.qna.analysis_report')}
          />
          <HighLightableBtn
            isActive={categorySorting === COMMUNITY_QNA_TYPE.GROWTH}
            onClick={() => setCategorySorting(COMMUNITY_QNA_TYPE.GROWTH)}
            label={t('community.qna.growth_change')}
          />
        </div>
        <div className="flex justify-end items-center text-[12px] font-[700] gap-2">
          <button
            type="button"
            style={{ color: order === OrderType.Likes ? '#363636' : '#969696' }}
            onClick={() => setOrder(OrderType.Likes)}
          >
            {t('community.sort_likes')}
          </button>
          <button
            type="button"
            style={{ color: order === OrderType.Latest ? '#363636' : '#969696' }}
            onClick={() => setOrder(OrderType.Latest)}
          >
            {t('community.sort_latest')}
          </button>
        </div>
      </div>
      {categorySorting === COMMUNITY_QNA_TYPE.ANALYSIS && (
        <div className="w-full flex justify-center items-center">
          <div className="flex justify-start items-center mt-3 text-[12px] font-[700] gap-1">
            <HighLightableBtn
              isActive={analysisUserTypeSorting === COMMUNITY_USER_TYPE.ALL}
              onClick={() => setAnalysisUserTypeSorting(COMMUNITY_USER_TYPE.ALL)}
              label={t('community.qna.total')}
            />
            <HighLightableBtn
              isActive={analysisUserTypeSorting === COMMUNITY_USER_TYPE.EXPERT}
              onClick={() => setAnalysisUserTypeSorting(COMMUNITY_USER_TYPE.EXPERT)}
              label={t('community.qna.expert')}
            />
            <HighLightableBtn
              isActive={analysisUserTypeSorting === COMMUNITY_USER_TYPE.USER}
              onClick={() => setAnalysisUserTypeSorting(COMMUNITY_USER_TYPE.USER)}
              label={t('community.qna.user')}
            />
          </div>
        </div>
      )}
      <div className="w-full mt-5">
        {list.map((post, idx) => {
          return (
            <div
              key={post.id}
              className="bg-white"
              onClick={() => openDrawer(post.id)}
              ref={idx === list.length - 1 ? lastItemRef : undefined}
            >
              <PostCard post={post} />
            </div>
          );
        })}
        {isLoading && (
          <div className="w-full py-5 text-center text-gray-500">
            {t('common.loading')}
          </div>
        )}
        <OptionDialog
          open={optionOpenId !== null}
          onClose={() => setOptionOpenId(null)}
          selectedId={optionOpenId || 0}
          reload={() => refetch()}
        />
        {complaintOpenId !== null && (
          <ComplaintDialog
            open={complaintOpenId}
            onClose={() => setComplaintOpenId(null)}
            targetType={COMPLAINT_TARGET_TYPE.QUESTION}
          />
        )}
      </div>
      <OneBtnAlert
        open={privatePostModalOpen}
        onClose={() => setPrivatePostModalOpen(false)}
        title={t('community.qna.private_alert_title')}
        description={t('community.qna.private_alert_desc')}
        btnText={t('common.confirm')}
        onClick={() => setPrivatePostModalOpen(false)}
      />
      <Drawer
        open={contentOpenId !== null}
        onClose={closeDrawer}
        anchor="right"
        sx={{
          '& .MuiDrawer-paper': {
            width: '100vw',
            maxWidth: '100vw',
          },
        }}
      >
        <PostDetail
          drawerOpen={contentOpenId !== null}
          onClickPrev={closeDrawer}
          postId={contentOpenId}
          reload={() => refetch()}
        />
      </Drawer>
    </div>
  );
}

export default Qna;
